import type { IMsalContextExtended } from '@festina-life/authentication'
import type { Currency, Language } from '@festina-life/core'
import { useLocalStorage } from '@festina-life/ui/hooks'
import { locale } from 'dayjs'
import { type ReactElement, useCallback, useLayoutEffect, useMemo } from 'react'

import { Provider, type SettingsContextType } from './settingsContext'

type Props = {
  currency: Currency
  children:
    | ReactElement
    | ((currentContext: SettingsContextType) => ReactElement | null | false)
  availableLanguages: Language[]
  defaultLanguage: Language
  authState: IMsalContextExtended
}

const isAvailableLanguage = (
  availableLanguages: Language[],
  language: Language | undefined,
): language is Language =>
  availableLanguages.some((availableLanguage) => availableLanguage === language)

/** Context Provider for the Life portal settings.  */
export const SettingsProvider = ({
  children,
  currency,
  defaultLanguage,
  availableLanguages,
  authState,
}: Props) => {
  const [language, setLanguage] = useLocalStorage<Language>('language')
  const [isInDebugMode, setIsInDebugMode] =
    useLocalStorage<boolean>('debugMode')

  const currentLanguage = isAvailableLanguage(availableLanguages, language)
    ? language
    : defaultLanguage

  useLayoutEffect(() => {
    locale(currentLanguage)
  }, [currentLanguage])

  const handleChangeLanguage = useCallback(
    (nextLanguage: Language) => {
      setLanguage(nextLanguage)

      // Changing the language requires a full page as Dayjs seem to not update accordingly.
      window.location.reload()
    },
    [setLanguage],
  )

  const value = useMemo(
    () =>
      ({
        authState,
        language: currentLanguage,
        changeLanguage: handleChangeLanguage,
        availableLanguages,
        currency,
        isInDebugMode: isInDebugMode ?? false,
        changeDebugMode: setIsInDebugMode,
      }) satisfies SettingsContextType,
    [
      authState,
      currentLanguage,
      handleChangeLanguage,
      availableLanguages,
      currency,
      isInDebugMode,
      setIsInDebugMode,
    ],
  )

  return (
    <Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </Provider>
  )
}
